import React from "react";
import { useLocalization } from "@fluent/react";
import type { Features } from "byzantine/src/Feature";
import { MODEL_STR } from "byzantine/src/Role";
import getAccountPermissionLabel, {
  getAccountPermissionParentLabel,
} from "./accountPermissionOptions";
import {
  AccountPermissionRow,
  getAccountPermissionRows,
} from "./RoleDisplayUtils";
import styles from "./OrganizationRoles.module.scss";

interface AccountPermissionsSummaryProps {
  /** Account-scoped permission model strings the account grants. */
  permissions: string[];
  features?: Features;
  /** Subtitle for the external transfer row, when the role is limited to some external accounts. */
  externalTransferSubtitle?: string;
}

/** Expanded body of an account row: its granted permissions, read only. */
export default function AccountPermissionsSummary({
  permissions,
  features,
  externalTransferSubtitle,
}: AccountPermissionsSummaryProps) {
  const { l10n } = useLocalization();
  const groups = getAccountPermissionRows(permissions, features);

  const rowLabel = (row: AccountPermissionRow) => {
    if (row.parent === "importPaymentFiles") {
      return l10n.getString("org-role-account-send-payments-by-file-import");
    }
    return row.parent
      ? getAccountPermissionParentLabel(row.parent, l10n)
      : getAccountPermissionLabel(row.modelStr ?? row.key, l10n);
  };

  // Subtitle parts, e.g. "with and without templates · instant delivery on". Modifiers are bold.
  const rowSubtitleParts = (
    row: AccountPermissionRow
  ): { text: string; isBold?: boolean }[] => {
    const parts: { text: string; isBold?: boolean }[] = [];
    if (row.templates === "both") {
      parts.push({
        text: l10n.getString("org-role-account-with-and-without-templates"),
      });
    } else if (row.templates === "templatesOnly") {
      parts.push({ text: l10n.getString("org-role-account-templates-only") });
    }
    row.details.forEach((detail) => {
      if (detail.kind === "importAch") {
        parts.push({
          text: l10n.getString("org-role-account-import-ach-files"),
        });
      } else if (detail.kind === "importWire") {
        parts.push({
          text: l10n.getString("org-role-account-import-wire-files"),
        });
      } else if (detail.modelStr === MODEL_STR.UNVERIFIED_FEDNOW_SEND) {
        parts.push({
          text: l10n.getString("org-role-account-instant-delivery-on"),
          isBold: true,
        });
      } else {
        parts.push({
          text: l10n.getString("org-role-account-reversals-on"),
          isBold: true,
        });
      }
    });
    if (row.modelStr === MODEL_STR.ACH_TRANSFER && externalTransferSubtitle) {
      parts.push({ text: externalTransferSubtitle });
    }
    return parts;
  };

  const renderRows = (title: string, rows: AccountPermissionRow[]) =>
    rows.length > 0 && (
      <div className={styles.accountPermissionGroup}>
        <p
          className={`${styles.accountPermissionGroupTitle} fontSize--xs fontWeight--bold`}
        >
          {title}
        </p>
        <div className={styles.accountPermissionRows}>
          {rows.map((row) => {
            const subtitleParts = rowSubtitleParts(row);
            return (
              <div key={row.key} className={styles.permissionItem}>
                <span
                  className={`narmi-icon-check ${styles.accountsIcon} ${styles.permissionCheck}`}
                  aria-hidden="true"
                />
                <div>
                  <p className={`fontSize--16px ${styles.permissionTitle}`}>
                    {rowLabel(row)}
                  </p>
                  {subtitleParts.length > 0 && (
                    <p
                      className={`fontSize--14px ${styles.permissionSubtitle}`}
                    >
                      {subtitleParts.map(({ text, isBold }, index) => (
                        <React.Fragment key={text}>
                          {index > 0 && " · "}
                          {isBold ? <strong>{text}</strong> : text}
                        </React.Fragment>
                      ))}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );

  return (
    <div className={styles.accountPermissionBody}>
      {renderRows(
        l10n.getString("org-role-account-group-money-movement"),
        groups.moneyMovement
      )}
      {renderRows(
        l10n.getString("org-role-account-group-manage-templates"),
        groups.manageTemplates
      )}
    </div>
  );
}
